import { lazy, useEffect, type ComponentType, type LazyExoticComponent } from 'react'
import { useLazyRouteGeneration } from '../stores/lazy-route-store'
import { clearChunkReloadMarker } from '../utils/chunk-load-error'

/**
 * Route-level code splitting. Every page is its own chunk, loaded on first
 * navigation; pages export named components, so map each to a default export
 * for React.lazy. Cross-cutting UI (call overlays, native bootstrap, the
 * onboarding tour / voice-call providers in main.tsx) stays eager — it must
 * mount globally, independent of the active route.
 *
 * The returned component is a thin wrapper rather than the `React.lazy` result
 * directly, so a failed chunk fetch stays recoverable: the wrapper rebuilds its
 * lazy component whenever the retry generation changes (see
 * `stores/lazy-route-store`), which is the only way to re-run a loader that
 * React has already memoised as rejected.
 */
export function lazyPage<K extends string>(
  loader: () => Promise<Record<K, ComponentType>>,
  name: K,
): ComponentType {
  let cachedGeneration = -1
  let cached: LazyExoticComponent<ComponentType> | null = null

  function LazyPage() {
    const generation = useLazyRouteGeneration()
    if (cached === null || cachedGeneration !== generation) {
      cachedGeneration = generation
      cached = lazy(async () => ({ default: (await loader())[name] }))
    }
    const Page = cached

    // Effects in this subtree are not committed until the chunk has resolved and
    // the surrounding Suspense boundary swaps the fallback out, so reaching here
    // proves chunk fetching works right now. Re-arm the one-shot reload guard so
    // a later deploy in this same session can still self-heal.
    useEffect(() => {
      clearChunkReloadMarker()
    }, [])

    return <Page />
  }

  LazyPage.displayName = `LazyPage(${name})`
  return LazyPage
}
